import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import SmartCard from '../../../shared/components/SmartCard';
import GraphQL from '../../../GraphQL';
import { professorSchedule } from '../../../shared/queries/TeacherRoom';
import {
  attendanceGradeDataSave,
  cycleClosingAttendanceWrapperByCycleClosing,
} from '../../../shared/queries/CycleClosing';
import Loading from '../../../shared/components/Loading';
import AttendanceStudentForm from './AttendanceStudentForm';
import { onShowAlert } from '../../../shared/utils/smartutils';
import { showNotificationAutoDestroy } from '../../../shared/components/ShowNotification';

const AttendanceStudentFormHandler = (props) => {
  const { t, dispatch, toggleFullScreen } = props;
  const [coursesData, setCoursesData] = useState([]);
  const [attendanceWrapper, setAttendanceWrapper] = useState({});
  const [loading, setLoading] = useState(false);
  const [formOn, setFormOn] = useState(false);
  const [notificationAlert, setNotificationAlert] = useState({
    status: false,
    title: '',
    color: '',
  });

  const showLoading = () => {
    setLoading(true);
  };

  const hideLoading = () => {
    setLoading(false);
  };

  const handleSubmit = (values) => {
    const mutation = attendanceGradeDataSave({
      ...values,
      cycleNumber: values.cycle.value,
    });
    showLoading();
    GraphQL(mutation, {})
      .then(() => {
        hideLoading();
        toggleFullScreen();
        showNotificationAutoDestroy(
          'success',
          t('success.title'),
          t('success.successfully-saved')
        );
      })
      .catch(() => {
        hideLoading();
        onShowAlert(
          'warning',
          'errors.title',
          'errors.error-to-save',
          setNotificationAlert
        );
      });
  };

  const subjectBySchoolClass = (data) => {
    return data
      .map((item) => ({
        ...item.subject,
        label: item.subject.name,
        value: item.subject.id,
        classId: item.schoolClass.id,
      }))
      .filter(
        (item, index, clone) =>
          clone.findIndex(
            ({ id, classId }) => id === item.id && classId === item.classId
          ) === index
      );
  };

  const schoolClassByCourse = (data) => {
    return data
      .map((item) => ({
        ...item.schoolClass,
        label: item.schoolClass.name,
        value: item.schoolClass.id,
      }))
      .filter(
        (item, index, clone) =>
          clone.findIndex(({ id }) => id === item.id) === index
      );
  };

  const createCyclesByCourseStep = (cycleQuantity) => {
    const listOfCycles = [];
    for (let i = 1; i <= cycleQuantity; i += 1) {
      listOfCycles.push({ value: i, label: `${i}º` });
    }
    return listOfCycles;
  };

  const createDataCourse = (value) => {
    const arrayList = [...value];
    const obj = {};
    arrayList.forEach((item) => {
      if (obj[item.schoolClass.courseStep.course.id]) {
        obj[item.schoolClass.courseStep.course.id] = [
          ...obj[item.schoolClass.courseStep.course.id],
          item,
        ];
      } else {
        obj[item.schoolClass.courseStep.course.id] = [item];
      }
    });
    Object.keys(obj).forEach((classId) => {
      obj[classId] = [
        { subjects: subjectBySchoolClass(obj[classId]) },
        { courseStep: obj[classId][0].schoolClass.courseStep },
        { schoolClass: schoolClassByCourse(obj[classId]) },
        {
          cycles: createCyclesByCourseStep(
            obj[classId][0].schoolClass.courseStep.cycleQuantity
          ),
        },
      ];
    });
    const arrayOfCourse = [];
    Object.keys(obj).forEach((classId) => {
      const newObj = {
        ...obj[classId].find((item) => item.subjects),
        ...obj[classId].find((item) => item.courseStep),
        ...obj[classId].find((item) => item.schoolClass),
        ...obj[classId].find((item) => item.cycles),
      };
      arrayOfCourse.push(newObj);
    });
    return arrayOfCourse;
  };

  const loadData = async () => {
    showLoading();
    const query = professorSchedule();
    GraphQL(query, {})
      .then((res) => {
        hideLoading();
        const arrayOfCourse = createDataCourse(res.professorSchedule);
        setCoursesData(arrayOfCourse);
      })
      .catch(() => {
        hideLoading();
        onShowAlert(
          'danger',
          'errors.title',
          'errors.error-to-loading',
          setNotificationAlert
        );
      });
  };

  const loadAttendanceGrid = (filter) => {
    const { schoolClass, subject, cycle, courseStep } = filter;
    showLoading();
    const query = cycleClosingAttendanceWrapperByCycleClosing(
      schoolClass.id,
      subject.id,
      cycle.value,
      courseStep.courseStep.id,
      schoolClass.courseStep.course.id
    );
    GraphQL(query, {})
      .then((res) => {
        hideLoading();
        if (res.cycleClosingAttendanceWrapperByCycleClosing) {
          if (
            res.cycleClosingAttendanceWrapperByCycleClosing.listStudentSummary
          ) {
            res.cycleClosingAttendanceWrapperByCycleClosing.listStudentSummary =
              res.cycleClosingAttendanceWrapperByCycleClosing.listStudentSummary.map(
                (sSummary) => ({
                  ...sSummary,
                  frequency:
                    sSummary.classQuantity > 0 &&
                    res.cycleClosingAttendanceWrapperByCycleClosing
                      .workDayQuantity > 0
                      ? (
                          (sSummary.classQuantity /
                            res.cycleClosingAttendanceWrapperByCycleClosing
                              .workDayQuantity) *
                          100
                        ).toFixed(2)
                      : 0,
                })
              );
          }
          setAttendanceWrapper(res.cycleClosingAttendanceWrapperByCycleClosing);
        } else {
          onShowAlert(
            'danger',
            t('notifications.error'),
            t('notifications.record-not-found'),
            setNotificationAlert
          );
          toggleFullScreen();
        }
      })
      .catch(() => {
        hideLoading();
        onShowAlert(
          'danger',
          'errors.title',
          'errors.error-to-loading',
          setNotificationAlert
        );
      });
  };

  useEffect(() => {
    loadData().then(() => setFormOn(true));
  }, []);

  return (
    <SmartCard>
      {formOn && (
        <>
          <Loading show={loading} />
          <AttendanceStudentForm
            onSubmit={handleSubmit}
            loadAttendanceGrid={loadAttendanceGrid}
            toggleFullScreen={toggleFullScreen}
            coursesData={coursesData}
            attendanceWrapper={attendanceWrapper}
            dispatch={dispatch}
            notificationAlert={notificationAlert}
          />
        </>
      )}
    </SmartCard>
  );
};

AttendanceStudentFormHandler.propTypes = {
  t: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  toggleFullScreen: PropTypes.func.isRequired,
};

export default withTranslation('common')(
  connect((state) => ({
    loading: state.loading,
  }))(AttendanceStudentFormHandler)
);
